import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Accordion, Form, Row, Col, Alert } from 'react-bootstrap';

const ResponseDetailsTab = ({ responses, survey }) => {
    const [filterValue, setFilterValue] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [stats, setStats] = useState({
        total: 0,
        complete: 0,
        incomplete: 0
    });

    // Calculate stats whenever responses change
    useEffect(() => {
        if (responses) {
            setStats({
                total: responses.length,
                complete: responses.filter(r => r.isSubmitted).length,
                incomplete: responses.filter(r => !r.isSubmitted).length
            });
        }
    }, [responses]);

    // Sort responses
    const sortedResponses = React.useMemo(() => {
        if (!responses) return [];

        let sortableItems = [...responses];

        sortableItems.sort((a, b) => {
            if (sortConfig.key === 'createdAt') {
                return sortConfig.direction === 'asc'
                    ? new Date(a.createdAt) - new Date(b.createdAt)
                    : new Date(b.createdAt) - new Date(a.createdAt);
            }

            // Add more sort options as needed
            return 0;
        });

        return sortableItems;
    }, [responses, sortConfig]);

    // Filter responses based on selection and search term
    const getFilteredResponses = () => {
        let filtered = sortedResponses;

        // Filter by completion status
        switch (filterValue) {
            case 'complete':
                filtered = filtered.filter(response => response.isSubmitted);
                break;
            case 'incomplete':
                filtered = filtered.filter(response => !response.isSubmitted);
                break;
            default:
                // No filtering needed for 'all'
                break;
        }

        // Filter by search term (if provided)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(response => {
                // Search in email
                if (response.email && response.email.toLowerCase().includes(term)) {
                    return true;
                }

                // Search in answers
                return response.answers.some(answer =>
                    (answer.answerText && answer.answerText.toLowerCase().includes(term)) ||
                    (answer.followups && answer.followups.some(followup =>
                        followup.answer && followup.answer.toLowerCase().includes(term)
                    ))
                );
            });
        }

        return filtered;
    };

    // Handle sorting
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Get question text by ID
    const getQuestionText = (questionId) => {
        const question = survey?.questions.find(q =>
            q._id.toString() === questionId.toString()
        );
        return question ? question.questionText : 'Unknown Question';
    };

    const filteredResponses = getFilteredResponses();

    return (
        <>
            {/* Stats Bar */}
            <Row className="mb-4">
                <Col md={4}>
                    <Alert variant="info">
                        <strong>Total Responses:</strong> {stats.total}
                    </Alert>
                </Col>
                <Col md={4}>
                    <Alert variant="success">
                        <strong>Completed:</strong> {stats.complete} ({stats.total > 0 ? Math.round((stats.complete / stats.total) * 100) : 0}%)
                    </Alert>
                </Col>
                <Col md={4}>
                    <Alert variant="warning">
                        <strong>Incomplete:</strong> {stats.incomplete} ({stats.total > 0 ? Math.round((stats.incomplete / stats.total) * 100) : 0}%)
                    </Alert>
                </Col>
            </Row>

            {/* Filter and Search Controls */}
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Filter by Status</Form.Label>
                        <Form.Select
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                        >
                            <option value="all">All Responses</option>
                            <option value="complete">Completed Only</option>
                            <option value="incomplete">Incomplete Only</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={8}>
                    <Form.Group>
                        <Form.Label>Search Responses</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Search by email or answer content..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>

            {/* Responses Table */}
            <Card>
                <Card.Body>
                    <Card.Title>Response Details ({filteredResponses.length} results)</Card.Title>

                    {filteredResponses.length === 0 ? (
                        <Alert variant="light" className="text-center my-4">
                            No responses match your current filters.
                        </Alert>
                    ) : (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Status</th>
                                    <th>Email</th>
                                    <th>Submission Date</th>
                                    <th>Last Activity</th>
                                    <th>Answers</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredResponses.map((response, index) => (
                                    <tr key={response._id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            {response.isSubmitted ? (
                                                <Badge bg="success">Completed</Badge>
                                            ) : (
                                                <Badge bg="warning">Incomplete</Badge>
                                            )}
                                        </td>
                                        <td>{response.email || 'Anonymous'}</td>
                                        <td>
                                            {formatDate(response.createdAt)}
                                        </td>
                                        <td>
                                            {formatDate(response.lastActivityAt || response.updatedAt)}
                                        </td>
                                        <td>
                                            <Accordion>
                                                <Accordion.Item eventKey="0">
                                                    <Accordion.Header>
                                                        View Answers ({response.answers.length})
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <Table size="sm" className="mb-0">
                                                            <thead>
                                                            <tr>
                                                                <th>Question</th>
                                                                <th>Answer</th>
                                                                <th>Follow-ups</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {response.answers.map((answer, ansIdx) => (
                                                                <tr key={answer._id}>
                                                                    <td>
                                                                        {getQuestionText(answer.questionId)}
                                                                    </td>
                                                                    <td>
                                                                        {answer.answerText || (
                                                                            <span className="text-muted">No answer</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        {answer.followups?.length > 0 ? (
                                                                            <Accordion>
                                                                                <Accordion.Item eventKey="0">
                                                                                    <Accordion.Header>
                                                                                        {answer.followups.length} follow-ups
                                                                                    </Accordion.Header>
                                                                                    <Accordion.Body>
                                                                                        {answer.followups.map((followup, fIdx) => (
                                                                                            <div key={fIdx} className="border-bottom mb-3 pb-3">
                                                                                                <div className="mb-2">
                                                                                                    <strong>Question {fIdx + 1}:</strong>
                                                                                                    <div className="border-start ps-3 mt-1">
                                                                                                        {followup.question}
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div>
                                                                                                    <strong>Answer:</strong>
                                                                                                    <div className="border-start ps-3 mt-1">
                                                                                                        {followup.answer || (
                                                                                                            <span className="text-muted">No answer</span>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </Accordion.Body>
                                                                                </Accordion.Item>
                                                                            </Accordion>
                                                                        ) : (
                                                                            <span className="text-muted">No follow-ups</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </Table>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            </Accordion>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default ResponseDetailsTab;